/**
 * FinancialSummary - Shared financial summary section for Dashboard
 *
 * Displays total assets, liabilities, income, and expenses with
 * inline percentile text. Used by both web and mobile dashboards.
 *
 * Layout differences are handled via responsive Tailwind classes.
 */
import { formatCurrency } from "../utils/formatting";
import { cn } from "../utils/cn";
import { getMarketAverages, calculatePercentile } from "../utils/marketData";

interface FinancialSummaryProps {
  totals: {
    assets: number;
    liabilities: number;
    income: number;
    expenses: number;
  };
  currentAge: number | null;
  currency: string;
  compact?: boolean;
}

interface SummaryCardProps {
  label: string;
  value: string;
  percentile: number;
  colorClass: string;
  compact?: boolean;
}

/** Return arrow and color class based on percentile ranking */
function getPercentileDisplay(percentile: number): {
  arrow: string;
  colorClass: string;
} {
  if (percentile > 60)
    return { arrow: "↑", colorClass: "summary-percentile--above" };
  if (percentile >= 40)
    return { arrow: "→", colorClass: "summary-percentile--average" };
  return { arrow: "↓", colorClass: "summary-percentile--below" };
}

/** Format ordinal suffix (1st, 2nd, 3rd, 4th, …) */
function ordinal(n: number): string {
  const rounded = Math.round(n);
  const s = ["th", "st", "nd", "rd"];
  const v = rounded % 100;
  return `${rounded}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function SummaryCard({
  label,
  value,
  percentile,
  colorClass,
  compact,
}: SummaryCardProps) {
  const { arrow, colorClass: pClass } = getPercentileDisplay(percentile);

  return (
    <div className={cn("summary-card", compact && "summary-card-compact")}>
      <p className="summary-card-label">{label}</p>
      <p
        className={cn(
          "summary-card-value",
          compact && "summary-card-value-compact",
          colorClass
        )}
      >
        {value}
      </p>
      <p className={cn("summary-percentile-text", pClass)}>
        <span>{arrow}</span>
        <span>{ordinal(percentile)} percentile</span>
      </p>
    </div>
  );
}

export function FinancialSummary({
  totals,
  currentAge,
  currency,
  compact = false,
}: FinancialSummaryProps) {
  const marketData = getMarketAverages(currentAge);

  const assetsPercentile = calculatePercentile(
    totals.assets,
    marketData.averageTotalAssets,
    true
  );
  const liabilitiesPercentile = calculatePercentile(
    totals.liabilities,
    marketData.averageTotalLiabilities,
    false
  );
  const incomePercentile = calculatePercentile(
    totals.income,
    marketData.averageMonthlyIncome,
    true
  );
  const expensesPercentile = calculatePercentile(
    totals.expenses,
    marketData.averageMonthlyExpenses,
    false
  );

  return (
    <div
      className={cn(
        "financial-summary",
        compact && "financial-summary-compact"
      )}
      id="financial-summary"
    >
      <h2
        className={cn("section-heading", compact && "section-heading-compact")}
      >
        Your Financial Summary
      </h2>
      <div className={cn(compact ? "summary-grid-compact" : "summary-grid")}>
        <SummaryCard
          label="Total Assets"
          value={formatCurrency(totals.assets, currency)}
          percentile={assetsPercentile}
          colorClass="summary-value-assets"
          compact={compact}
        />
        <SummaryCard
          label="Total Liabilities"
          value={formatCurrency(totals.liabilities, currency)}
          percentile={liabilitiesPercentile}
          colorClass="summary-value-liabilities"
          compact={compact}
        />
        <SummaryCard
          label="Monthly Income"
          value={formatCurrency(totals.income, currency)}
          percentile={incomePercentile}
          colorClass="summary-value-income"
          compact={compact}
        />
        <SummaryCard
          label="Monthly Expenses"
          value={formatCurrency(totals.expenses, currency)}
          percentile={expensesPercentile}
          colorClass="summary-value-expenses"
          compact={compact}
        />
      </div>
    </div>
  );
}
